import { getMaskedNumber } from "../../Utils/Constants/MaskedNumber";
import { clearTokens, setTokens } from "../TokenStore";

// Auth tokens are held in memory by TokenStore. These keys are kept only to
// delete tokens persisted by earlier builds.
const legacyTokenKeys = ["access-token", "refresh-token"];

export enum localStorageConstants {
  vuaId = "vuaId",
  phoneNumber = "phoneNumber",
  UserId = "userid",
  SessionId = "sessionid",
  Redirect = "redirect",
  SrcRef = "srcref",
  TxnId = "txnid",
  FiuId = "fiuId",
}

export function setPhoneNumber(phoneNumber: string) {
  localStorage.setItem(localStorageConstants.phoneNumber, phoneNumber);
}

export function setVuaId(vuaId: string) {
  localStorage.setItem(localStorageConstants.vuaId, vuaId);
}

export function getLocalStorageItem(item: string) {
  return localStorage.getItem(item);
}

// Tokens go to memory only; the non-secret session details stay in localStorage.
export function setLoginSession(res: any) {
  setTokens(res);
  setPhoneNumber(getMaskedNumber(res.phoneNumber));
  setVuaId(res.vuaId);
  setSessionId(res.session_state)
}

export function clearLoginSession() {
  clearTokens();
  setPhoneNumber("");
  setVuaId("");
  setSessionId("");
  clearRedirectIdentifiers();
}

// PAN/DOB decoded from an FIU redirect, used to prefill account discovery. Kept only for the
// redirect flow: removed on logout, after a successful discovery, and once older than the TTL.
const REDIRECT_IDENTIFIERS_KEY = "additionalIdentifier";
export const REDIRECT_IDENTIFIERS_TTL_MS = 30 * 60 * 1000;

export function setRedirectIdentifiers(identifiers: unknown[], now = Date.now()) {
  if (!identifiers.length) {
    clearRedirectIdentifiers();
    return;
  }
  localStorage.setItem(REDIRECT_IDENTIFIERS_KEY, JSON.stringify({ savedAt: now, identifiers }));
}

/** Current redirect identifiers; stale, legacy-format or corrupt values are removed. */
export function getRedirectIdentifiers(now = Date.now()): any[] {
  const raw = localStorage.getItem(REDIRECT_IDENTIFIERS_KEY);
  if (!raw) {
    return [];
  }
  try {
    const stored = JSON.parse(raw);
    const age = now - stored?.savedAt;
    if (
      Array.isArray(stored?.identifiers) &&
      typeof stored.savedAt === "number" &&
      age >= 0 &&
      age <= REDIRECT_IDENTIFIERS_TTL_MS
    ) {
      return stored.identifiers;
    }
  } catch {
    // Corrupt value: fall through and remove it.
  }
  clearRedirectIdentifiers();
  return [];
}

export function clearRedirectIdentifiers() {
  localStorage.removeItem(REDIRECT_IDENTIFIERS_KEY);
}

/** Removes PAN/DOB left over from an earlier, abandoned redirect flow. */
export function removeStaleRedirectIdentifiers(now = Date.now()) {
  getRedirectIdentifiers(now);
}

export function removeLegacyTokens() {
  legacyTokenKeys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

export function setUserId(userId: string) {
  localStorage.setItem(localStorageConstants.UserId, userId);
}

export function setSessionId(sessionId: string) {
  localStorage.setItem(localStorageConstants.SessionId, sessionId);
}

export function setRedirect(redirect: string) {
  localStorage.setItem(localStorageConstants.Redirect, redirect);
}

export function setSrcRef(srcRef: string) {
  localStorage.setItem(localStorageConstants.SrcRef, srcRef);
}

export function setTxnId(txnId: string) {
  localStorage.setItem(localStorageConstants.TxnId, txnId);
}

export function setFiuId(fiuId: string) {
  localStorage.setItem(localStorageConstants.FiuId, fiuId);
}

export function setDecryptedCredential(res: any) {
  setUserId(res.userid);
  setRedirect(res.redirect);
  setSrcRef(res.srcref);
  setSessionId(res.sessionid);
  setTxnId(res.txnid);
  setFiuId(res.fiuId);
}
